import {api} from "../api";
import * as BootstrapModal from "@/shared/ui/bootstrapModal";
import * as Button from "@/shared/utils/button";
import {ExportJobStatus, ExportType} from "@/shared/timrTypes";

export interface DownloadStatusConfig {
	exportJobId: string;
	exportType: ExportType;
	allowCancellation: boolean; // visibility the cancel button
	exportStatusBaseUrl?: string; // url for status poll (default: "export/status.json")
	loadingMessage?: string; // custom text after progress indicator icon (default: "Loading data...")
	timeoutDuration?: number; // poll frequency (default: DEFAULT_TIMEOUT_DURATION = 1000ms)
}

export class DownloadStatus {

	private static DEFAULT_TIMEOUT_DURATION = 1000;
	private static DEFAULT_STATUS_BASE_URL = "export/status.json";
	private static CANCEL_EXPORT_JOB_URL = "export/cancel.json";

	private config: DownloadStatusConfig;
	private modal: BootstrapModal.Modal;
	private timeout: number;
	private pollRequest: JQuery.jqXHR<unknown>;

	constructor(config: DownloadStatusConfig) {
		this.config = config;

		/* create modal html content, there are some configurations:
		 * - allowCancellation: if true the bottom margin gets adjusted (the footer including the cancel button is visible)
		 * - loadingMessage: if available, the default loading message get replaced by this one
		 */
		const dom = $("<div>").addClass(this.config.allowCancellation ? "mb-5" : "")
			.append($("<h4>").addClass("no-margin-top").text(this.config.loadingMessage ? this.config.loadingMessage : messages["downloadStatus.loadingMessage.default"]))
			.append($("<i>").addClass("fas fa-spinner-third fa-fast-spin mt-15 mb-15").prop("aria-hidden", "true"))
			.append($("<div>").addClass("input-info-text")
				.append($("<i>").addClass("far fa-info-circle position-left").prop("aria-hidden", "true"))
				.append($("<span>").text(messages["downloadStatus.loadingMessage.info"])));

		this.modal = new BootstrapModal.Modal({
			title: "holiday.header.create",
			message: dom,
			destroyOnClose: false,
			css: {header: "hidden", content: "download-status", dialog: "modal-vertical-centered"},
			buttons: [{
				text: "dialog.cancel",
				css: "btn-default",
				action: (modal, button) => this.cancelExportJob(button)
			}],
			footer: this.config.allowCancellation // hide footer if cancellation must not be possible
		});

		this.pollExportJobStatus();
		this.show();
	}

	public show() {
		this.modal.show();
	}

	public close() {
		this.modal.close();
	}

	/**
	 * Cancels the current exportJob (defined in config)
	 * The dialog gets closed and the button reset afterwards
	 * @param button
	 * @private
	 */
	private cancelExportJob(button: JQuery) {
		// abort request if currently running
		this.pollRequest?.abort();

		// clear timeout for export polling
		clearTimeout(this.timeout);

		api(DownloadStatus.CANCEL_EXPORT_JOB_URL)
			.put({exportJobId: this.config.exportJobId})
			.done(() => {
				this.close();
				Button.setLoading(button);
			});
	}

	/**
	 * Queries the current status via an api request. Depending on the returned status, different actions are performed:
	 * - Running, Waiting: status is queried again after timeout (becomes a loop if the export job takes a while)
	 * - success: file is downloaded by a download export url call, status modal is closed
	 * - error: warning modal will be opened, status modal closed
	 * - cancelled: status dialog closed
	 * @private
	 */
	private pollExportJobStatus() {
		this.pollRequest = api(this.config.exportStatusBaseUrl ? this.config.exportStatusBaseUrl : DownloadStatus.DEFAULT_STATUS_BASE_URL)
			.get({exportJobId: this.config.exportJobId})
			.done((data: { status: ExportJobStatus }) => {
				const status = data.status;

				if (ExportJobStatus.Running === status || ExportJobStatus.Waiting === status) {
					this.timeout = setTimeout(
						() => this.pollExportJobStatus(),
						this.config.timeoutDuration ? this.config.timeoutDuration : DownloadStatus.DEFAULT_TIMEOUT_DURATION
					);
				} else if (ExportJobStatus.Success === status) {
					const target = this.config.exportType === ExportType.Html ? "_blank" : "_self";
					window.open("/timr/export/download/" + this.config.exportJobId, target);
					this.close();
				} else {
					if (ExportJobStatus.Error === status) {
						BootstrapModal.warn({
							title: "downloadStatus.error.title",
							message: "downloadStatus.error"
						});
					}
					this.close();
				}
			});
	}
}
